import React, { useMemo, useState } from 'react';
import './schedule.css';
import { STRINGS, detectLang } from './i18n';
import { buildWeek, coverageEnd, addDays, mondayOf, localTodayISO, parseISO } from './model';
import { rangeLabel } from './format';
import useSchedule from './useSchedule';
import { DesktopTable, MobileMatrix, MixedBadge } from './Timetable';

const Chevron = ({ dir }) => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
    <path fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" d={dir === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'} />
  </svg>
);

function Header({ t }) {
  return (
    <header className="tc-sched__head">
      <h2 className="tc-sched__title" id="tc-sched-title">{t.title}</h2>
    </header>
  );
}

function WeekNavigator({ week, t, onPrev, onNext }) {
  return (
    <div className="tc-sched__nav">
      <button type="button" className="tc-sched__btn tc-sched__btn--nav" onClick={week.canPrev ? onPrev : undefined} aria-disabled={!week.canPrev} aria-label={t.prev}>
        <Chevron dir="prev" />
      </button>
      <p className="tc-sched__range" aria-live="polite" aria-atomic="true">
        <span className="tc-sched__sr">{t.week}: </span>
        {rangeLabel(t.locale, week.weekStart, week.weekEnd)}
      </p>
      <button type="button" className="tc-sched__btn tc-sched__btn--nav" onClick={week.canNext ? onNext : undefined} aria-disabled={!week.canNext} aria-label={t.next}>
        <Chevron dir="next" />
      </button>
    </div>
  );
}

function Loading({ t }) {
  return (
    <div className="tc-sched__state" role="status" aria-live="polite">
      <span className="tc-sched__spinner" aria-hidden="true" />
      <p>{t.loading}</p>
    </div>
  );
}
function Empty({ t, week }) {
  return (
    <div className="tc-sched__state tc-sched__state--empty" role="status">
      <p className="tc-sched__statetitle">{t.empty}</p>
      {week ? <p>{t.emptyWeek}</p> : null}
    </div>
  );
}
function ErrorState({ t, onRetry }) {
  return (
    <div className="tc-sched__state tc-sched__state--error" role="alert">
      <p className="tc-sched__statetitle">{t.error}</p>
      <p>{t.errorHint}</p>
      <button type="button" className="tc-sched__btn tc-sched__btn--retry" onClick={onRetry}>{t.retry}</button>
    </div>
  );
}

function Notice({ t, hasMixed }) {
  return (
    <footer className="tc-sched__notice">
      {hasMixed ? (
        <p className="tc-sched__legend">
          <MixedBadge t={t} />
          <span><strong>{t.mixedRoute}.</strong> {t.mixedExplain}</span>
        </p>
      ) : null}
      <p>{t.notice}</p>
    </footer>
  );
}

export default function ScheduleApp() {
  const lang = useMemo(() => detectLang(), []);
  const t = STRINGS[lang];
  const urls = useMemo(() => ({ cozumel: window.miPlugin && window.miPlugin.Cozumel, calica: window.miPlugin && window.miPlugin.Calica }), []);
  const data = useSchedule(urls);
  const todayISO = useMemo(() => localTodayISO(), []);
  const currentWeekStart = useMemo(() => mondayOf(todayISO), [todayISO]);
  const [weekStart, setWeekStart] = useState(currentWeekStart);
  const [selectedIndex, setSelectedIndex] = useState(() => (parseISO(todayISO).getUTCDay() + 6) % 7);

  let week = null;
  let globalEmpty = false;
  if (data.status === 'ready') {
    const origins = [{ id: 'cozumel', entries: data.cozumel }, { id: 'calica', entries: data.calica }];
    const end = coverageEnd(data.cozumel, data.calica);
    globalEmpty = end === '' || end < currentWeekStart;
    week = buildWeek({ weekStart, todayISO, selectedIndex, origins, currentWeekStart });
  }

  return (
    <section className="tc-sched" aria-labelledby="tc-sched-title" lang={t.locale} data-state={data.status === 'ready' && globalEmpty ? 'empty' : data.status}>
      <Header t={t} />
      <div className="tc-sched__body">
        {data.status === 'loading' ? <Loading t={t} /> : null}
        {data.status === 'error' ? <ErrorState t={t} onRetry={data.retry} /> : null}
        {data.status === 'ready' && globalEmpty ? <Empty t={t} /> : null}
        {data.status === 'ready' && !globalEmpty ? (
          <>
            <WeekNavigator week={week} t={t} onPrev={() => setWeekStart(addDays(weekStart, -7))} onNext={() => setWeekStart(addDays(weekStart, 7))} />
            {week.hasData ? (
              <>
                <DesktopTable week={week} t={t} onSelect={setSelectedIndex} />
                <MobileMatrix week={week} t={t} onSelect={setSelectedIndex} />
              </>
            ) : (
              <Empty t={t} week />
            )}
            <Notice t={t} hasMixed={week.hasMixed} />
          </>
        ) : null}
      </div>
    </section>
  );
}
